/**
 * Resolves template variables in text fields
 * Example: "Price: {{item.product.price}}" becomes "Price: 29.99"
 *
 * @param text - The text containing template variables
 * @param element - The data object to resolve variables against
 * @returns The text with resolved variables
 */
type DateContext = {
	currentMonth: string
	currentMonthShort: string
	nextMonth: string
	currentYear: string
	currentMonthNumber: string
}

const MONTH_NAMES = [
	'January', 'February', 'March', 'April', 'May', 'June',
	'July', 'August', 'September', 'October', 'November', 'December'
]

const MONTH_SHORT_NAMES = [
	'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
	'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
]

function buildDateContext(): DateContext {
	const now = new Date()
	const month = now.getMonth()
	return {
		currentMonth: MONTH_NAMES[month],
		currentMonthShort: MONTH_SHORT_NAMES[month],
		nextMonth: MONTH_NAMES[(month + 1) % 12],
		currentYear: String(now.getFullYear()),
		currentMonthNumber: String(month + 1)
	}
}

export function resolveDateTokens(
	text: string | null | undefined,
	extra?: Record<string, string>
): string {
	if (!text) return ''
	const ctx: Record<string, string> = { ...buildDateContext(), ...extra }
	return text.replace(/\{\{(\w+)\}\}/g, (match, key) =>
		key in ctx ? ctx[key] : match
	)
}

export function resolveRichTextDateTokens<T extends { text: string }>(field: T[]): T[] {
	return field.map((block) => ({ ...block, text: resolveDateTokens(block.text) }))
}

export default function resolveTemplateVariables(
	text: string | null | undefined,
	element: any
): string {
	if (!text) return ''

	return text.replace(/\{\{([^}]+)\}\}/g, (match, path) => {
		const keys = path.trim().split('.')
		let value = element

		for (const key of keys) {
			if (value && typeof value === 'object' && key in value) {
				value = value[key]
			} else {
				return match // Return original if path not found
			}
		}

		// Handle number formatting - if it's a number, check if it's a whole number
		if (typeof value === 'number') {
			// If the number is a whole number (like 50.0), display as integer
			return value % 1 === 0 ? Math.floor(value).toString() : value.toString()
		}

		// Handle string numbers that might be floats
		if (typeof value === 'string' && !Number.isNaN(Number(value))) {
			const numValue = Number(value)
			return numValue % 1 === 0 ? Math.floor(numValue).toString() : value
		}

		return value?.toString() || match
	})
}
